/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Table component for labels import wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.3.0
 */

import { Fragment, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../components/common';
import InfoBlock from '../../../../components/common/InfoBlock/InfoBlock';
import { loadTableListAction } from '../../../../components/Table/actions/tableActions';
import { ITableField, ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableCount,
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableIsLoading,
} from '../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil, { ETableColumnUtilHideFilters } from '../../../../components/Table/TableColumnUtil';
import { ILoadTableListPayload, TableNameType } from '../../../../components/Table/types/tableTypes';
import { EColorStatus } from '../../../../modules/app/appConstants';
import { makeSelectTranslationCommon } from '../../../../modules/internationalization/intSelectors';
import { ICommonPageTranslation, ICommonTranslation } from '../../../../modules/internationalization/types';
import { ICommonPageTranslationTitle } from '../../../../modules/internationalization/types/pages/common';
import {
    ILabelsImportTableTranslation,
    ILabelsImportTranslation,
} from '../../../../modules/internationalization/types/wizards/labelsImport';
import { guardComponentType } from '../../../../modules/role/reducer/guardReducer';
import {
    makeSelectGuardComponents,
    makeSelectIsColumnResizeEnabled,
} from '../../../../modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { stringFormatParams } from '../../../../utils/stringFormat';
import { labelsImportInitValidatedList } from '../../labelsImportActions';
import { ELabelsImportField, ELabelsImportLabelsValidationStatus } from '../../labelsImportConstants';
import { ILabelsImportLabelsValidationInfo } from '../../labelsImportReducer';
import {
    makeSelectLabelsImportIsMaxRowsExceeded,
    makeSelectLabelsImportMaxCountSubscribers,
} from '../../labelsImportSelectors';
import { LabelsImportInfoBlockContainerStyled } from '../styled/LabelsImportStepFinalStyled';
import {
    getColumnCellType,
    getColumnFilterType,
    getColumnFixedType,
    labelsImportColumnNames,
    labelsImportColumnsWidth,
} from './LabelsImportTableCells';

interface ILabelsImportTableProps {
    labelsImportTranslation: ILabelsImportTranslation;
    labelsValidationInfo: ILabelsImportLabelsValidationInfo;
}

export type TLabelsImportTable = ILabelsImportTableProps & IConnectedProps & IDispatchProps;

export const LabelsImportTable = (props: TLabelsImportTable) => {
    const tableColumnUtil = new TableColumnUtil<ELabelsImportField,
        ICommonPageTranslation<ILabelsImportTableTranslation> & ICommonTranslation>({
            cellConfig: labelsImportColumnsWidth,
            componentsFeature: props.componentsFeature,
            fields: props.fields as Array<ITableField<ELabelsImportField>>,
            isTableResizeEnabled: props.isTableResizeEnabled,
            getColumnCellType,
            getColumnFilterType,
            getColumnFixedType,
            columnNames: labelsImportColumnNames,
            hideFiltersType: ETableColumnUtilHideFilters.EMPTY,
            isDisableSort: true,
            translation: {
                ...props.translationCommon,
                table: { ...props.labelsImportTranslation.table },
                title: {} as ICommonPageTranslationTitle,
            },
        },
    );

    const getInfoBlockStatus = () => {
        return props.labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.INTERRUPTED ||
        props.isMaxRowsExceeded ? EColorStatus.WARNING : EColorStatus.SUCCESS;
    };

    const getInfoBlockMessage = () => {
        const {
            validatedLabelsMessage,
            interruptedValidationMessage,
            maxRowsExceededMessage,
        } = props.labelsImportTranslation;

        return props.labelsValidationInfo.status === ELabelsImportLabelsValidationStatus.INTERRUPTED ?
            interruptedValidationMessage : props.isMaxRowsExceeded ?
                stringFormatParams(maxRowsExceededMessage, { maxCountSubscribers: props.maxCountSubscribers }) :
                stringFormatParams(validatedLabelsMessage, props.labelsValidationInfo);
    };

    useEffect(() => {
        props.initValidatedList();
    }, []);

    return (
        <Fragment>
            <LabelsImportInfoBlockContainerStyled>
                <InfoBlock
                    colorStatus={getInfoBlockStatus()}
                >
                    {getInfoBlockMessage()}
                </InfoBlock>
            </LabelsImportInfoBlockContainerStyled>

            <Table
                columns={tableColumnUtil.customizeTableColumns()}
                counter={props.tableCount}
                data={props.data}
                isDensity={props.isDensity}
                isLoading={props.isLoading}
                loadTableList={props.loadTableList}
                scrollbarBottom='64px'
                setTableSort={() => {
                }}
                sort={{}}
                tableDataName="labelsImport"
            />
        </Fragment>
    );
};

interface IConnectedProps {
    componentsFeature: guardComponentType;
    data: ITableReducer[TableNameType]['data'];
    fields: ITableReducer[TableNameType]['fields'];
    isDensity: boolean;
    isLoading: boolean;
    isMaxRowsExceeded: boolean;
    isTableResizeEnabled: boolean;
    maxCountSubscribers: number;
    tableCount: number;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.labelsImport),
    fields: makeSelectorTableFields(TableNameType.labelsImport),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(TableNameType.labelsImport),
    isMaxRowsExceeded: makeSelectLabelsImportIsMaxRowsExceeded(),
    isTableResizeEnabled: makeSelectIsColumnResizeEnabled(),
    maxCountSubscribers: makeSelectLabelsImportMaxCountSubscribers(),
    tableCount: makeSelectorTableCount(TableNameType.labelsImport),
    translationCommon: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    initValidatedList: () => void;
    loadTableList: (payload?: ILoadTableListPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    initValidatedList: () => dispatch(labelsImportInitValidatedList()),
    loadTableList: (payload) => dispatch(loadTableListAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LabelsImportTable);
